import React, { useState, useEffect } from "react";
import { Modal, Spin } from "antd";
import { FaChartLine, FaWeight, FaTape, FaChartPie, FaHeartbeat } from "react-icons/fa";
import moment from "moment";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import VitalService from "../../../Services/VitalService";

const trendVitals = [
  { code: "WE", label: "Weight", color: "#10b981" },
  { code: "WA", label: "Waist", color: "#3b82f6" },
  { code: "HP", label: "Hip", color: "#6366f1" },
  { code: "CH", label: "Chest", color: "#f43f5e" },
];

const TrendCard = ({ vitalCode, patientId, color, label }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!patientId) return;
      setLoading(true);
      try {
        const fromDate = moment().subtract(30, "days").format("MM/DD/YYYY");
        const toDate = moment().format("MM/DD/YYYY");
        const payload = {
          patientId,
          vitalCode: [vitalCode],
          fromDate,
          toDate,
          status: 1,
        };
        const result = await VitalService.getVitalForGraph(payload);
        const rawData = result?.data || [];
        
        const formattedData = rawData
          .filter(item => item.value && item.value !== 0 && item.value !== "0")
          .sort((a, b) => moment(a.recordedDateTime).valueOf() - moment(b.recordedDateTime).valueOf())
          .map(item => ({
            date: moment(item.recordedDateTime).format("DD/MM"),
            value: parseFloat(item.value),
            fullDate: moment(item.recordedDateTime).format("DD MMM YYYY")
          }))
          .slice(-7);

        setData(formattedData);
      } catch (e) {
        console.error(`Error fetching trend for ${vitalCode}:`, e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [vitalCode, patientId]);

  if (loading) return <div className="h-full flex items-center justify-center"><Spin size="small" /></div>;
  if (data.length === 0) return (
    <div className="h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
      <FaChartLine className="text-xl mb-2 opacity-30" />
      <span className="text-[10px] font-medium">No data points</span>
    </div>
  );

  const gradId = `grad_${vitalCode}`;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={color} stopOpacity={0.3} />
            <stop offset="95%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
        <XAxis
          dataKey="date"
          axisLine={false}
          tickLine={false}
          tick={{ fill: "#9ca3af", fontSize: 10, fontWeight: 500 }}
        />
        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: "#9ca3af", fontSize: 10 }}
        />
        <Tooltip
          contentStyle={{
            background: "#fff",
            border: "none",
            borderRadius: "12px",
            boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
            fontSize: "11px"
          }}
          labelStyle={{ fontWeight: "bold", color: "#374151", marginBottom: "4px" }}
          labelFormatter={(val, payload) => payload[0]?.payload?.fullDate || val}
        />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradId})`}
          dot={{ r: 4, fill: color, stroke: "#fff", strokeWidth: 2 }}
          activeDot={{ r: 6, fill: color, stroke: "#fff", strokeWidth: 2 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const DietVitalsTrends = ({ open, onCancel, patient }) => {
  const [recentVitals, setRecentVitals] = useState({
    WE: "-",
    WA: "-",
    HP: "-",
    CH: "-",
  });
  const [loadingRecent, setLoadingRecent] = useState(false);

  useEffect(() => {
    const fetchRecent = async () => {
      if (!patient?.patientId || !open) return;
      setLoadingRecent(true);
      try {
        const res = await VitalService.getPatientRecentVitals(patient.patientId);
        if (res.data && Array.isArray(res.data)) {
          const data = res.data;
          const getValue = (code) => {
            const v = data.find((item) => item.vitalCode === code && item.value !== 0 && item.value !== "0");
            if (!v) return "-";
            const unit = code === "WE" ? " kg" : code === "HP" ? " ratio" : " cm";
            return v.value + unit;
          };
          setRecentVitals({
            WE: getValue("WE"),
            WA: getValue("WA"),
            HP: getValue("HP"),
            CH: getValue("CH"),
          });
        }
      } catch (error) {
        console.error("Error fetching recent vitals:", error);
      } finally {
        setLoadingRecent(false);
      }
    };
    fetchRecent();
  }, [patient?.patientId, open]);

  const cards = [
    { key: "WE", label: "Weight", icon: <FaWeight />, color: "#10b981", bg: "#f0fdf4" },
    { key: "WA", label: "Waist", icon: <FaTape />, color: "#3b82f6", bg: "#eff6ff" },
    { key: "HP", label: "Hip", icon: <FaChartPie />, color: "#6366f1", bg: "#eef2ff" },
    { key: "CH", label: "Chest", icon: <FaHeartbeat />, color: "#f43f5e", bg: "#fff1f2" },
  ];

  return (
    <Modal
      title={`Vitals Trends - ${patient?.patientName || patient?.name || ""}`}
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={900}
    >
      <div className="flex gap-4 mb-6">
        {cards.map((card) => (
          <div
            key={card.key}
            className="flex-1 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center gap-3 transition-transform hover:scale-105"
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-sm border"
              style={{ color: card.color, backgroundColor: card.bg, borderColor: `${card.color}20` }}
            >
              {card.icon}
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                {card.label}
              </p>
              <p className="text-base font-black text-gray-800 mt-0.5">
                {loadingRecent ? <Spin size="small" /> : recentVitals[card.key]}
              </p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-6 p-2">
        {trendVitals.map((v) => (
          <div key={v.code} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: `${v.color}15` }}
              >
                <FaChartLine style={{ color: v.color }} className="text-sm" />
              </div>
              <h3 className="text-sm font-bold text-gray-700">{v.label} Trend</h3>
            </div>
            <div className="h-48">
              <TrendCard
                vitalCode={v.code}
                patientId={patient?.patientId}
                color={v.color}
                label={v.label}
              />
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
};

export default DietVitalsTrends;
